import cx from "classnames";
import { CSSProperties, FC, JSX, ReactNode, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { InfoBadge } from "@app/shared/InfoBadge";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import { truncateStringWithEllipsis } from "@app/shared/utility/truncateStringWithEllipsis";

export type QesActiveFiltersBarVariant = "accordion-style" | "minimal" | "flat";

export interface FilterChipDescriptor {
  /** Accessible label for the chip's dismiss button. */
  readonly ariaLabel?: string;
  /** Visible chip content. When a string and truncateAt is set, content will be truncated with a tooltip. */
  readonly content: string | JSX.Element;
  /**
   * Category label used to group chips. Used only by the accordion-style variant.
   * flat and minimal variants ignore this field.
   */
  readonly group?: string;
  /** Size of the dismiss icon (passed to InfoBadge). Defaults to InfoBadge's default (1rem). */
  readonly iconSize?: string;
  /** Stable React key and base for the chip's data-testid ({testIdPrefix}-{key}). */
  readonly key: string;
  /** Called when the user dismisses this chip. When absent, the chip renders without an X icon. */
  readonly onDismiss?: () => void;
  /**
   * Optional sub-category within the group. Used only by the accordion-style variant.
   * When present, chips sharing the same group are clustered under sub-headings within that group.
   */
  readonly subgroup?: string;
  /** If set, string content longer than this many characters is truncated and a tooltip shows the full text. */
  readonly truncateAt?: number;
}

export interface QesActiveFiltersBarProps {
  /** Additional class names applied to the root element. */
  readonly className?: string;
  /** Chip descriptors to display. The component renders nothing when this array is empty. */
  readonly chips: readonly FilterChipDescriptor[];
  /**
   * Row count at which the chip area clips with overflow:hidden and the "Show N more" toggle
   * appears. The collapse/expand feature is off unless this prop is provided — omit it to show
   * all chip rows with no toggle. Applies to flat and minimal variants only; ignored by accordion-style.
   */
  readonly collapsedRows?: number;
  /**
   * Optional element rendered in the accordion header alongside the title (accordion-style only).
   * Ignored by flat and minimal variants.
   */
  readonly headerAction?: React.ReactNode;
  /**
   * Maximum row count when expanded. Chip area becomes vertically scrollable when chip content
   * exceeds this height. Defaults to 6. Only meaningful when collapsedRows is set.
   * Applies to flat and minimal variants only; ignored by accordion-style.
   */
  readonly maxRows?: number;
  /** If provided, renders a Clear All button that calls this function. */
  readonly onClearAll?: () => void;
  /**
   * Prefix used to build chip data-testid values: `{testIdPrefix}-{chip.key}`.
   * Defaults to "qes-active-filter". Pass a feature-specific prefix to preserve
   * existing automation test IDs when migrating to this shared component.
   */
  readonly testIdPrefix?: string;
  /**
   * Visual treatment:
   * - flat: blue background bar with "Active Filters:" label (NPM)
   * - accordion-style: collapsible accordion with chips grouped by chip.group (Provider Hub)
   * - minimal: frameless chip strip with optional Clear All (NCM)
   */
  readonly variant: QesActiveFiltersBarVariant;
  /** Optional content rendered after the Clear All button (flat and minimal variants only). */
  readonly trailingContent?: ReactNode;
}

// All row-height constants must stay in sync with _qes-active-filters-bar.scss.
// CHIP_ROW_HEIGHT_PX         = flat InfoBadge height (~24px at 12px font) + row-gap = $qes-afb-chip-row-height
// MINIMAL_CHIP_ROW_HEIGHT_PX = minimal InfoBadge height (~31px, inherits page font) + row-gap + buffer
//                              = $qes-afb-minimal-chip-row-height
// The collapsed budget holds collapsedRows *full* rows (collapsedRows * rowHeight) and mirrors the
// flat `.is-collapsed` max-height in the SCSS. No clip-offset is subtracted: each row-height already
// bundles a row-gap, so subtracting more clipped the last row and falsely tripped the overflow check,
// surfacing "Show N more" at exactly collapsedRows rows.
// Flat applies a font-size: 12px override to .qes-info-badge, making its chips ~24px tall.
// Minimal inherits the page font (~14-16px), making chips ~31px tall — a separate row-height constant is required.
const CHIP_ROW_HEIGHT_PX = 36;
const MINIMAL_CHIP_ROW_HEIGHT_PX = 38;
const DEFAULT_MAX_ROWS = 6;

function ChipBadge({ chip, testIdPrefix }: { chip: FilterChipDescriptor; testIdPrefix: string }): JSX.Element {
  const shouldTruncate =
    chip.truncateAt != null &&
    typeof chip.content === "string" &&
    chip.content.length > chip.truncateAt;

  const displayContent: string | JSX.Element = shouldTruncate
    ? truncateStringWithEllipsis(chip.content as string, chip.truncateAt!)
    : chip.content;

  const badge = (
    <InfoBadge
      ariaLabel={chip.ariaLabel}
      content={displayContent}
      icon={chip.onDismiss ? undefined : null}
      iconSize={chip.iconSize}
      onClick={chip.onDismiss}
      testId={`${testIdPrefix}-${chip.key}`}
    />
  );

  if (shouldTruncate) {
    return (
      <TooltipWrapper position="top" text={chip.content as string}>
        {badge}
      </TooltipWrapper>
    );
  }

  return badge;
}

export const QesActiveFiltersBar: FC<QesActiveFiltersBarProps> = ({ className, chips, collapsedRows, headerAction, maxRows = DEFAULT_MAX_ROWS, onClearAll, testIdPrefix = "qes-active-filter", trailingContent, variant }) => {
  const labelId = useId();
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !collapsedRows) {
      setIsOverflowing(false);
      return;
    }

    if (expanded) {
      // Minimal chips are taller than flat's because flat overrides .qes-info-badge font-size.
      const effectiveRowHeight = variant === "minimal" ? MINIMAL_CHIP_ROW_HEIGHT_PX : CHIP_ROW_HEIGHT_PX;
      // Auto-collapse if content fits within collapsedRows full rows — the same budget as the
      // SCSS `.is-collapsed` clamp (collapsedRows * rowHeight, no clip-offset).
      if (el.scrollHeight <= collapsedRows * effectiveRowHeight) {
        setExpanded(false);
      }
      return;
    }

    const check = () => {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    };

    check();

    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [chips, collapsedRows, maxRows, expanded, variant]);

  const groupedChips = useMemo<[string | undefined, [string | undefined, FilterChipDescriptor[]][]][]>(() => {
    if (variant !== "accordion-style") return [];

    const map = new Map<string | undefined, Map<string | undefined, FilterChipDescriptor[]>>();

    for (const chip of chips) {
      const g = chip.group;
      const sg = chip.subgroup;
      if (!map.has(g)) map.set(g, new Map());
      const subMap = map.get(g)!;
      if (!subMap.has(sg)) subMap.set(sg, []);
      subMap.get(sg)!.push(chip);
    }

    return Array.from(map.entries()).map(([group, subMap]) => [group, Array.from(subMap.entries())]);
  }, [chips, variant]);

  if (chips.length === 0) return null;

  const collapsibleStyle = collapsedRows
    ? { "--qes-afb-collapsed-rows": collapsedRows, "--qes-afb-max-rows": maxRows } as CSSProperties
    : undefined;

  const showToggle = (
    <>
      {!expanded && isOverflowing && (
        <QESButton
          aria-expanded={false}
          className="qes-active-filter-bar__show-toggle"
          onClick={() => setExpanded(true)}
          qesTestId="qes-active-filter-bar-show-toggle"
          qesType="none"
        >
          Show more
        </QESButton>
      )}
      {expanded && (
        <QESButton
          aria-expanded={true}
          className="qes-active-filter-bar__show-toggle"
          onClick={() => {
            // Reset scroll before collapsing so the chip area always shows from row 1.
            // Resetting after setExpanded is unreliable once overflow switches to hidden.
            if (containerRef.current) containerRef.current.scrollTop = 0;
            setExpanded(false);
          }}
          qesTestId="qes-active-filter-bar-show-toggle"
          qesType="none"
        >
          Show less
        </QESButton>
      )}
    </>
  );

  switch (variant) {
    case "accordion-style":
      return (
        <div className={`qes-active-filter-bar qes-active-filter-bar--accordion-style${className ? ` ${className}` : ""}`} data-testid="qes-active-filter-bar">
          <AccordionContainer>
            <Accordion defaultOpen={false} headerAction={headerAction} testId="qes-active-filter-bar-accordion" title={`Applied Filters: ${chips.length}`}>
              <div className="qes-active-filter-bar__body">
                {groupedChips.map(([group, subgroupEntries]) => (
                  <div key={group ?? "__ungrouped"} className={`qes-active-filter-bar__group${subgroupEntries.length > 1 || subgroupEntries[0]?.[0] != null ? " qes-active-filter-bar__group--stacked" : ""}`}>
                    {group && <strong className="qes-active-filter-bar__group-title">{group}:</strong>}
                    {subgroupEntries.length === 1 && subgroupEntries[0][0] == null ? (
                      <ul className="qes-active-filter-bar__chips" role="list">
                        {subgroupEntries[0][1].map((chip) => (
                          <li key={chip.key}>
                            <ChipBadge chip={chip} testIdPrefix={testIdPrefix} />
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="qes-active-filter-bar__group-body">
                        {subgroupEntries.map(([subgroup, subChips]) => (
                          <div key={subgroup ?? "__no-subgroup"} className="qes-active-filter-bar__subgroup">
                            {subgroup && <strong className="qes-active-filter-bar__subgroup-title">{subgroup}:</strong>}
                            <ul className="qes-active-filter-bar__chips" role="list">
                              {subChips.map((chip) => (
                                <li key={chip.key}>
                                  <ChipBadge chip={chip} testIdPrefix={testIdPrefix} />
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Accordion>
          </AccordionContainer>
        </div>
      );
    case "flat":
      return (
        <div
          className={cx("qes-active-filter-bar", "qes-active-filter-bar--flat", {
            "is-collapsed": !!collapsedRows && !expanded,
            "is-expanded": expanded,
          }, className)}
          data-testid="qes-active-filter-bar"
          style={collapsibleStyle}
        >
          <div className="qes-active-filter-bar__header">
            <span className="qes-active-filter-bar__label" id={labelId}>Active Filters:</span>
            {onClearAll && (
              <QESButton
                className="qes-active-filter-bar__clear-all"
                onClick={onClearAll}
                qesTestId="qes-active-filter-bar-clear-all"
                qesType="none"
              >
                Clear All
              </QESButton>
            )}
            {trailingContent}
          </div>
          <div ref={containerRef} className="qes-active-filter-bar__chip-area">
            <ul aria-labelledby={labelId} className="qes-active-filter-bar__chips" role="list">
              {chips.map((chip) => (
                <li key={chip.key} data-chip-key={chip.key}>
                  <ChipBadge chip={chip} testIdPrefix={testIdPrefix} />
                </li>
              ))}
            </ul>
          </div>
          {showToggle}
        </div>
      );
    case "minimal":
      return (
        <div
          className={cx("qes-active-filter-bar", "qes-active-filter-bar--minimal", {
            "is-collapsed": !!collapsedRows && !expanded,
            "is-expanded": expanded,
          }, className)}
          data-testid="qes-active-filter-bar"
          style={collapsibleStyle}
        >
          <div className="qes-active-filter-bar__row">
            <div ref={containerRef} className="qes-active-filter-bar__chip-area">
              <ul className="qes-active-filter-bar__chips" role="list">
                {chips.map((chip) => (
                  <li key={chip.key} data-chip-key={chip.key}>
                    <ChipBadge chip={chip} testIdPrefix={testIdPrefix} />
                  </li>
                ))}
              </ul>
            </div>
            {onClearAll && (
              <QESButton
                className="qes-active-filter-bar__clear-all"
                onClick={onClearAll}
                qesTestId="qes-active-filter-bar-clear-all"
                qesType="none"
              >
                Clear All
              </QESButton>
            )}
            {trailingContent}
          </div>
          {showToggle}
        </div>
      );
  }
};
